import type { ComponentType, ReactNode } from "react";
import { cn } from "@/lib/utils";

type IconContainerSize = "sm" | "md" | "lg";
type IconContainerVariant = "muted" | "accent" | "destructive";

interface IconContainerProps {
  icon?: ComponentType<{ className?: string }>;
  size?: IconContainerSize;
  variant?: IconContainerVariant;
  children?: ReactNode;
  className?: string;
}

const sizeClasses: Record<IconContainerSize, { box: string; icon: string }> = {
  sm: { box: "size-8 rounded-lg", icon: "size-4" },
  md: { box: "size-10 rounded-xl", icon: "size-5" },
  lg: { box: "size-12 rounded-xl", icon: "size-6" },
};

const variantClasses: Record<IconContainerVariant, string> = {
  muted: "bg-muted text-muted-foreground",
  accent: "bg-rose-500/10 text-rose-500 ring-1 ring-rose-500/10",
  destructive: "bg-destructive/10 text-destructive",
};

export function IconContainer({
  icon: Icon,
  size = "md",
  variant = "muted",
  children,
  className,
}: IconContainerProps) {
  const { box, icon: iconClass } = sizeClasses[size];
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center",
        box,
        variantClasses[variant],
        className
      )}
    >
      {Icon && <Icon className={iconClass} />}
      {children}
    </div>
  );
}
